import * as PIXI from 'pixi.js';
import { useMapStore } from '../stores/mapStore';

/**
 * IsoGroundPlane
 *
 * Renders the battle map texture as an isometric ground plane.
 * The flat 2D map texture is projected onto the isometric diamond shape
 * using Pixi.js mesh with custom UV mapping.
 *
 * For Phase 1 we use a simpler approach: skew the sprite to approximate
 * the isometric ground angle (a rotation + scale transform).
 *
 * The ground plane occupies the full grid area in isometric space.
 */

const PLACEHOLDER_COLOR = 0x1a1410;
const GRID_TINT = 0x2a2420;

export class IsoGroundPlane {
  public container: PIXI.Container;

  private sprite: PIXI.Sprite | null = null;
  private placeholder: PIXI.Graphics;
  private unsubscribe: (() => void) | null = null;
  private loadGeneration = 0;
  private currentTextureUrl: string | null = null;

  private gridWidth: number;
  private gridHeight: number;
  private tileWidth: number;
  private tileHeight: number;

  constructor(
    gridWidth: number,
    gridHeight: number,
    tileWidth: number,
    tileHeight: number
  ) {
    this.container = new PIXI.Container();
    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;

    // Create placeholder ground (dark parchment diamond)
    this.placeholder = new PIXI.Graphics();
    this.container.addChild(this.placeholder);
    this.drawPlaceholder();

    // Subscribe to map store for texture changes
    this.subscribeToStore();
  }

  private drawPlaceholder(): void {
    const g = this.placeholder;
    g.clear();

    // Draw filled diamond for the ground area
    const w = this.gridWidth;
    const h = this.gridHeight;

    // Four corners of the isometric grid
    const top = this.gridToScreen(0, 0);
    const right = this.gridToScreen(w, 0);
    const bottom = this.gridToScreen(w, h);
    const left = this.gridToScreen(0, h);

    // Dark ground fill
    g.beginFill(PLACEHOLDER_COLOR, 1);
    g.moveTo(top.x, top.y);
    g.lineTo(right.x, right.y);
    g.lineTo(bottom.x, bottom.y);
    g.lineTo(left.x, left.y);
    g.closePath();
    g.endFill();

    // Subtle diamond border
    g.lineStyle(2, GRID_TINT, 0.6);
    g.moveTo(top.x, top.y);
    g.lineTo(right.x, right.y);
    g.lineTo(bottom.x, bottom.y);
    g.lineTo(left.x, left.y);
    g.closePath();
  }

  private gridToScreen(gx: number, gy: number): { x: number; y: number } {
    const halfW = this.tileWidth / 2;
    const halfH = this.tileHeight / 2;
    return {
      x: (gx - gy) * halfW,
      y: (gx + gy) * halfH,
    };
  }

  /** Pick whichever map actually has a texture; prefer the iso slot if both do. */
  private getIsoMap() {
    const state = useMapStore.getState();
    const iso = state.activeIsoMap;
    const top = state.activeMap;
    if (iso?.baseTexture?.colorUrl) return iso;
    if (top?.baseTexture?.colorUrl) return top;
    return iso ?? top;
  }

  private subscribeToStore(): void {
    let lastUrl: string | null = null;
    this.unsubscribe = useMapStore.subscribe((state) => {
      const map = state.activeIsoMap?.baseTexture?.colorUrl
        ? state.activeIsoMap
        : state.activeMap?.baseTexture?.colorUrl
          ? state.activeMap
          : null;
      const nextUrl = map?.baseTexture?.colorUrl ?? null;
      if (nextUrl !== lastUrl) {
        console.log(`[IsoGround] Texture change: ${lastUrl ? 'had' : 'no'} → ${nextUrl ? 'loaded' : 'cleared'} (${map?.name ?? 'none'})`);
        lastUrl = nextUrl;
        this.loadTexture(nextUrl);
      }
    });

    // Load initial texture
    const map = this.getIsoMap();
    const initialUrl = map?.baseTexture?.colorUrl ?? null;
    if (initialUrl) {
      lastUrl = initialUrl;
      this.loadTexture(initialUrl);
    }
  }

  private async loadTexture(url: string | null): Promise<void> {
    const gen = ++this.loadGeneration;

    if (!url) {
      this.clearSprite();
      this.placeholder.visible = true;
      return;
    }

    if (url === this.currentTextureUrl) return;

    try {
      const texture = await PIXI.Texture.fromURL(url);

      // Stale check
      if (gen !== this.loadGeneration) return;

      this.clearSprite();
      this.currentTextureUrl = url;

      // Create sprite and apply isometric transform (skip for natively iso textures)
      const spr = new PIXI.Sprite(texture);
      this.sprite = spr;

      const mapStyle = this.getIsoMap()?.style;
      if (mapStyle === 'isometric') {
        // Natively generated iso texture — just scale to fit the grid diamond
        this.applyNativeIsoScale(spr);
      } else {
        // Top-down texture — warp into iso projection
        this.applyIsoTransform(spr);
      }

      // Insert behind placeholder
      this.container.addChildAt(spr, 0);
      this.placeholder.visible = false;
    } catch {
      if (gen !== this.loadGeneration) return;
      console.warn('[IsoGroundPlane] Failed to load texture:', url);
      this.placeholder.visible = true;
    }
  }

  /**
   * Scale a natively isometric texture to fill the grid diamond.
   * No rotation/skew — the image is already iso-perspective.
   */
  private applyNativeIsoScale(spr: PIXI.Sprite): void {
    // The iso grid diamond has width = (gridWidth + gridHeight) * tileWidth / 2
    // and height = (gridWidth + gridHeight) * tileHeight / 2
    const diamondW = (this.gridWidth + this.gridHeight) * (this.tileWidth / 2);
    const diamondH = (this.gridWidth + this.gridHeight) * (this.tileHeight / 2);

    spr.width = diamondW;
    spr.height = diamondH;

    // Center the sprite on the grid diamond's top corner
    spr.anchor.set(0.5, 0);
    spr.position.set(0, -diamondH * 0.05); // slight upward nudge for visual alignment
  }

  /**
   * Apply isometric projection to a flat sprite.
   *
   * We use a CSS-matrix–style approach: rotate 45° then scale Y by 0.5
   * to get the classic 2:1 isometric look. The sprite is then sized
   * to fill the grid diamond.
   */
  private applyIsoTransform(spr: PIXI.Sprite): void {
    const totalW = this.gridWidth * this.tileWidth;
    const totalH = this.gridHeight * this.tileHeight;

    // Scale sprite to match grid world size (before iso transform)
    const scaleX = (totalW / spr.texture.width);
    const scaleY = (totalH / spr.texture.height);

    // Iso transform: rotate 45° then squash Y by 50%
    const angle = Math.PI / 4; // 45 degrees
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const ySquash = 0.5;

    // Combined matrix: Scale → Rotate(45°) → ScaleY(0.5)
    // This maps the flat rectangle onto the isometric diamond
    spr.setTransform(
      0, 0,                          // position (adjusted by pivot)
      1, 1,                          // scale (handled in matrix)
      0,                             // rotation
      0, 0,                          // skew
      0, 0                           // pivot
    );

    // Apply the combined transform via the local transform matrix
    const m = spr.transform.localTransform;
    m.a = cos * scaleX;           // scaleX * cos(45)
    m.b = sin * scaleX * ySquash; // scaleX * sin(45) * ySquash
    m.c = -sin * scaleY;          // scaleY * -sin(45)
    m.d = cos * scaleY * ySquash; // scaleY * cos(45) * ySquash
    m.tx = 0;
    m.ty = 0;

    // The sprite anchor at (0,0) maps to grid corner (0,0) in iso space
    // which is the top of the diamond — correct position
  }

  private clearSprite(): void {
    if (this.sprite) {
      this.container.removeChild(this.sprite);
      // Don't destroy shared textures from Pixi cache
      this.sprite.destroy({ texture: false });
      this.sprite = null;
      this.currentTextureUrl = null;
    }
  }

  refresh(): void {
    const map = this.getIsoMap();
    this.loadTexture(map?.baseTexture?.colorUrl || null);
  }

  destroy(): void {
    this.unsubscribe?.();
    this.clearSprite();
    this.placeholder.destroy();
    this.container.destroy({ children: true });
  }
}
